<template>
  <div class="flex flex-col gap-4">
    <ZoneAlert
      v-if="errors.length"
      color="red"
      icon="error"
      role="alert"
      data-testid="step-errors"
    >
      <p
        v-for="message in errors"
        :key="message"
      >
        {{ message }}
      </p>
    </ZoneAlert>
    <section
      class="flex max-h-[min(58rem,calc(100dvh-var(--space-above-and-below)))] flex-col rounded-lg border border-gray-200 bg-white p-5"
      style="--space-above-and-below: calc(var(--topbar-height) + 12.8rem)"
      :data-testid="testid"
    >
      <div class="flex shrink-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="min-w-0 flex-1">
          <h2 class="text-xl font-semibold text-gray-900">{{ t(`step_names.${stepId}`) }}</h2>
          <p class="mt-1 text-sm text-gray-500">{{ description }}</p>
        </div>
        <div
          v-if="!showSummary"
          class="flex shrink-0 items-center gap-3"
        >
          <ZoneButton
            v-if="completed || !unskippable"
            reset
            class="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium text-gray-500 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-700"
            data-testid="step-skip"
            @click="$emit(completed ? 'cancel' : 'skip')"
          >
            {{ completed ? t('cancel') : t('skip') }}
          </ZoneButton>
          <slot name="action">
            <ZoneButton
              color="primary_alt"
              :disabled="!!hint"
              :tooltip="hint"
              :type="form ? 'submit' : 'button'"
              :form
              size-class="h-9 px-4 text-sm"
              class="whitespace-nowrap"
              data-testid="step-save"
              @click="$emit('save')"
            >
              {{ saveLabel }}
            </ZoneButton>
          </slot>
        </div>
      </div>

      <template v-if="showSummary">
        <div class="-mx-5 mt-4 flex shrink-0 items-center justify-between gap-3 border-y border-t-gray-100 border-b-green-100 bg-green-50 px-5 py-3">
          <p class="flex items-center gap-3 text-base font-semibold text-gray-900">
            <CheckBurst
              class="size-6"
              :rings="[{ color: 'border-green-400', delay: '60ms' }]"
              stamp-class="bg-green-100"
              stamp-delay="40ms"
              data-testid="completed-check"
            >
              <svg
                viewBox="0 0 24 24"
                class="size-4"
                fill="none"
              >
                <path
                  class="check-tick stroke-green-500"
                  style="--d: 220ms"
                  d="M6.5 12.5 L10 16 L17.5 8"
                  stroke-width="2.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </CheckBurst>
            {{ t('step_completed') }}
          </p>
          <ZoneButton
            variant="outline"
            color="gray_alt"
            size-class="px-4 py-2 text-sm font-medium!"
            class="whitespace-nowrap text-gray-700! hover:bg-gray-50!"
            data-testid="step-edit"
            @click="editing = true"
          >
            {{ editLabel }}
          </ZoneButton>
        </div>
        <div class="-mx-5 -mb-5 flex min-h-0 flex-col overflow-y-auto px-5 py-5">
          <slot name="summary" />
        </div>
      </template>
      <div
        v-else
        class="-mx-5 -mb-5 mt-3 flex min-h-0 flex-col overflow-y-auto border-t px-5 pb-5"
        :class="scrolled ? 'border-gray-200' : 'border-transparent'"
        data-testid="step-body"
        @scroll="scrolled = $event.target.scrollTop > 0"
      >
        <slot />
      </div>
      <slot name="dialog" />
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneAlert from 'zone/components/base/Alert.vue';
import ZoneButton from 'zone/components/base/Button.vue';
import CheckBurst from '~/components/experiments/0239_getting_started_checklist/CheckBurst.vue';

const { stepId, description, editLabel, completed, unskippable, errors, saving, saveLabel = null, saveHint = null, form = null } = defineProps({
  stepId: { type: String, required: true },
  description: { type: String, required: true },
  editLabel: { type: String, required: true },
  completed: { type: Boolean },
  unskippable: { type: Boolean },
  errors: { type: Array, required: true },
  saving: { type: Boolean },
  saveLabel: { type: String },
  saveHint: { type: String },
  form: { type: String },
});

defineEmits(['save', 'skip', 'cancel']);

const { t } = useI18n();

const editing = ref(false);
const scrolled = ref(false);

const testid = computed(() => `step-${stepId.replaceAll('_', '-')}`);
const showSummary = computed(() => completed && !editing.value);
const hint = computed(() => (saving ? t('save_in_progress') : saveHint));
</script>


<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "step_completed": "Step complete"
  }
}
</i18n>
